// Liste historique des diagnostics - Dashboard
// Tokens shadcn uniquement, zero couleur en dur

import { Check, Trash2, RefreshCw, FileText, Award } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Card, CardContent, CardDescription, CardTitle, Button } from '../ui'
import { Badge } from '../ui/badge'

// Affichage des casques (1-5) avec images SVG
function CasquesDisplay({ count, max = 5 }: { count: number; max?: number }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: max }, (_, i) => (
        <img
          key={i}
          src={i < count ? '/images/casque-actif.svg' : '/images/casque-desactive.svg'}
          alt={i < count ? 'Casque actif' : 'Casque inactif'}
          className="w-6 h-6"
        />
      ))}
    </div>
  )
}

// Barre de progression circulaire
function ProgressCircle({ percent }: { percent: number }) {
  const radius = 18
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (percent / 100) * circumference

  return (
    <div className="relative w-12 h-12">
      <svg className="w-12 h-12 transform -rotate-90">
        <circle
          cx="24"
          cy="24"
          r={radius}
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
          className="text-muted"
        />
        <circle
          cx="24"
          cy="24"
          r={radius}
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="text-primary"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-foreground">
        {percent}%
      </span>
    </div>
  )
}

export interface DiagnosticHistoryItem {
  id: string
  entrepriseId: string
  metierIds: string[]
  nom?: string | null    // Story 16 : libellé personnalisé (null/undefined si pas renseigné)
  statut: 'en_cours' | 'termine'
  dateDebut: string | Date
  versionNumber?: number
  versionDiag?: number   // Numéro de mise à jour (1, 2, 3…)
  parentId?: string      // ID du diagnostic parent (null = V1)
  synced?: boolean
  entrepriseNom?: string
  entrepriseSiret?: string | null
  userName?: string
  metierNoms?: string[]
  resultat?: any
  attestation?: { statut: string; badgeDore: boolean } | null
  progression?: number // Pourcentage de questions repondues
  reponsesCount?: number // _count.reponses du backend
  // Sélections du tunnel (persistance)
  selectedThemes?: string[]
  selectedSolutions?: Array<{ solutionId: string; metierIds: string[] }>
  // Stats d'actions et themes
  themesChoisis?: string[]
  actionsEnCours?: number
  actionsTerminees?: number
}

interface DiagnosticsHistoryProps {
  diagnostics: DiagnosticHistoryItem[]
  onResume: (diagnostic: DiagnosticHistoryItem) => void
  onUpdate?: (diagnostic: DiagnosticHistoryItem) => void
  onExportSynthese?: (diagnostic: DiagnosticHistoryItem) => void
  onAttestation?: (diagnostic: DiagnosticHistoryItem) => void
  onDelete: (diagnosticId: string) => void
  className?: string
}

export default function DiagnosticsHistory({
  diagnostics,
  onResume,
  onUpdate,
  onExportSynthese,
  onAttestation,
  onDelete,
  className = '',
}: DiagnosticsHistoryProps) {
  if (diagnostics.length === 0) {
    return (
      <div className={cn('text-center py-8', className)}>
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-1">
          Aucun diagnostic
        </h3>
        <p className="text-sm text-muted-foreground">
          Commencez votre premier diagnostic pour évaluer vos pratiques.
        </p>
      </div>
    )
  }

  // Separer diagnostics en cours et termines
  const enCours = diagnostics.filter((d) => d.statut === 'en_cours')
  const termines = diagnostics.filter((d) => d.statut === 'termine')

  return (
    <div className={cn('space-y-6', className)}>
      {/* Diagnostics en cours */}
      {enCours.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
              En cours ({enCours.length})
            </h3>
          </div>
          <div className="space-y-3">
            {enCours.map((diagnostic) => (
              <DiagnosticCard
                key={diagnostic.id}
                diagnostic={diagnostic}
                onResume={onResume}
                onDelete={onDelete}
                isEnCours
              />
            ))}
          </div>
        </div>
      )}

      {/* Diagnostics termines */}
      {termines.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">
            Terminés ({termines.length})
          </h3>
          <div className="space-y-3">
            {termines.map((diagnostic) => (
              <DiagnosticCard
                key={diagnostic.id}
                diagnostic={diagnostic}
                onResume={onResume}
                onUpdate={onUpdate}
                onExportSynthese={onExportSynthese}
                onAttestation={onAttestation}
                onDelete={onDelete}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface DiagnosticCardProps {
  diagnostic: DiagnosticHistoryItem
  onResume: (diagnostic: DiagnosticHistoryItem) => void
  onUpdate?: (diagnostic: DiagnosticHistoryItem) => void
  onExportSynthese?: (diagnostic: DiagnosticHistoryItem) => void
  onAttestation?: (diagnostic: DiagnosticHistoryItem) => void
  onDelete: (diagnosticId: string) => void
  isEnCours?: boolean
}

function DiagnosticCard({
  diagnostic,
  onResume,
  onUpdate,
  onExportSynthese,
  onAttestation,
  onDelete,
  isEnCours = false,
}: DiagnosticCardProps) {
  const dateFormatted = new Date(diagnostic.dateDebut).toLocaleDateString(
    'fr-FR',
    { day: 'numeric', month: 'long', year: 'numeric' }
  )

  return (
    <Card
      className={cn(
        'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md active:scale-[0.99]',
        isEnCours ? 'border-primary/30 bg-primary/5' : 'bg-card border-border'
      )}
    >
      <CardContent className="p-4">
        {/* Ligne 1: Nom + Version + Bouton supprimer */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <CardTitle className="text-base font-semibold truncate">
              {diagnostic.entrepriseNom || 'Entreprise'}
            </CardTitle>
            <Badge variant="secondary" className="text-xs flex-shrink-0">
              V{diagnostic.versionDiag ?? diagnostic.versionNumber ?? 1}
            </Badge>
          </div>
          <button
            onClick={() => onDelete(diagnostic.id)}
            className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors flex-shrink-0"
            title="Supprimer ce diagnostic"
          >
            <Trash2 className="size-4" />
          </button>
        </div>

        {/* Ligne 2: Metiers en badges */}
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          {(diagnostic.metierNoms || diagnostic.metierIds).map((name, i) => (
            <Badge key={i} variant="outline" className="text-xs font-normal">
              {name}
            </Badge>
          ))}
        </div>

        {/* Ligne 3: Date + Createur */}
        <CardDescription className="text-sm mb-3">
          {dateFormatted}
          {diagnostic.userName && (
            <> · Créé par {diagnostic.userName}</>
          )}
        </CardDescription>

        {/* Ligne 3: Casques ou Progression */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {isEnCours ? (
              <div className="flex items-center gap-2">
                <ProgressCircle percent={diagnostic.progression || 0} />
                <span className="text-sm text-muted-foreground">En cours</span>
              </div>
            ) : diagnostic.resultat ? (
              <div className="flex items-center gap-2">
                <CasquesDisplay count={diagnostic.resultat.casques} />
                <Badge variant="outline" className="text-xs">
                  <Check className="size-3 mr-1" />
                  Terminé
                </Badge>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <CasquesDisplay count={0} />
                <span className="text-sm text-muted-foreground/60">Non calculé</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant={isEnCours ? 'default' : 'outline'}
              onClick={() => onResume(diagnostic)}
            >
              {isEnCours ? 'Reprendre' : 'Consulter'}
            </Button>
            {!isEnCours && onUpdate && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onUpdate(diagnostic)}
                className="gap-1.5"
              >
                <RefreshCw className="size-3.5" />
                Mise à jour
              </Button>
            )}
            {!isEnCours && onExportSynthese && (
              <button
                onClick={() => onExportSynthese(diagnostic)}
                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                title="Exporter la synthèse"
              >
                <FileText className="size-4" />
              </button>
            )}
            {!isEnCours && onAttestation && (
              <button
                onClick={() => (diagnostic.resultat?.casques ?? 0) > 0 && onAttestation(diagnostic)}
                disabled={(diagnostic.resultat?.casques ?? 0) === 0}
                className={cn(
                  'p-1.5 rounded-lg transition-colors',
                  (diagnostic.resultat?.casques ?? 0) > 0
                    ? 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    : 'text-muted-foreground/30 cursor-not-allowed'
                )}
                title={(diagnostic.resultat?.casques ?? 0) > 0 ? "Générer l'attestation" : 'Attestation indisponible'}
              >
                <Award className="size-4" />
              </button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export { CasquesDisplay, ProgressCircle }
